import { WidgetUploadError } from './upload-thread-file.js';
import { isBrowserOnline, isNetworkError } from './is-network-error.js';
import { isRetryableQueueError } from './is-retryable-queue-error.js';
import { normalizeOutboundImageFile } from './normalize-outbound-image.js';
import {
  getOutboundImageBlob,
  putOutboundImageBlob,
  removeOutboundImageBlob,
} from './outbound-image-store.js';
import {
  isImageQueueEntry,
  readOutboundQueue,
  removeOutboundMessage,
  updateOutboundMessage,
  type OutboundQueueEntry,
} from './outbound-queue.js';
import type {
  SendImageMessageFn,
  SendMessageFn,
  SendMessageResult,
} from './send-message.js';

export type FlushOutboundQueueOptions = {
  scopeKey: string;
  sendMessage: SendMessageFn;
  sendImageMessage?: SendImageMessageFn;
  getThreadId: () => number | string | null;
  resolveThreadId?: () => Promise<number | string | null>;
  onConfirmed: (
    clientId: string,
    result: SendMessageResult,
    kind: 'text' | 'image',
    entry: OutboundQueueEntry,
  ) => void | Promise<void>;
  onImageUploadComplete?: (clientId: string, url: string) => void;
  onFailed?: (clientId: string, error: unknown) => void;
};

export type FlushOutboundQueueResult = {
  flushed: number;
  remaining: number;
  stoppedOnError: boolean;
};

export async function flushOutboundQueue(
  options: FlushOutboundQueueOptions,
): Promise<FlushOutboundQueueResult> {
  if (!isBrowserOnline()) {
    const remaining = readOutboundQueue(options.scopeKey).length;
    return { flushed: 0, remaining, stoppedOnError: false };
  }

  let flushed = 0;
  let stoppedOnError = false;

  while (true) {
    const queue = readOutboundQueue(options.scopeKey);
    const entry = queue[0];
    if (!entry) break;

    try {
      const result = await sendQueuedEntry(entry, options);
      removeOutboundMessage(options.scopeKey, entry.clientId);
      await removeOutboundImageBlob(options.scopeKey, entry.clientId);
      const kind = isImageQueueEntry(entry) ? 'image' : 'text';
      await options.onConfirmed(entry.clientId, result, kind, entry);
      flushed += 1;
    } catch (error) {
      options.onFailed?.(entry.clientId, error);
      if (isRetryableQueueError(error) || isNetworkError(error) || !isBrowserOnline()) {
        stoppedOnError = true;
        break;
      }
      removeOutboundMessage(options.scopeKey, entry.clientId);
      await removeOutboundImageBlob(options.scopeKey, entry.clientId);
    }
  }

  return {
    flushed,
    remaining: readOutboundQueue(options.scopeKey).length,
    stoppedOnError,
  };
}

async function sendQueuedEntry(
  entry: OutboundQueueEntry,
  options: FlushOutboundQueueOptions,
): Promise<SendMessageResult> {
  if (!isImageQueueEntry(entry)) {
    const threadId = options.getThreadId() ?? entry.threadId;
    return options.sendMessage({
      threadId,
      text: entry.text,
      clientId: entry.clientId,
      kind: 'text',
      history: entry.history,
      greetingId: entry.greetingId,
      page: entry.page,
      messageId: entry.messageId,
    });
  }

  if (entry.uploadedUrl && entry.text.trim()) {
    const threadId = options.getThreadId() ?? entry.threadId;
    return options.sendMessage({
      threadId,
      text: entry.text,
      clientId: entry.clientId,
      kind: 'image',
      page: entry.page,
      messageId: entry.messageId,
    });
  }

  if (!options.sendImageMessage) {
    throw new Error('[Teo Widget] sendImageMessage required for queued images');
  }

  const file = await getOutboundImageBlob(
    options.scopeKey,
    entry.clientId,
    entry.fileName,
    entry.mimeType,
  );
  if (!file) {
    throw new Error('[Teo Widget] Queued image blob missing');
  }

  const normalized = await normalizeOutboundImageFile(file);

  let threadId = options.getThreadId() ?? entry.threadId;
  if (threadId == null || Number(threadId) <= 0) {
    threadId = (await options.resolveThreadId?.()) ?? null;
  }
  if (threadId == null || Number(threadId) <= 0) {
    throw new WidgetUploadError(
      'NO_THREAD',
      '[Teo Widget] thread_id is required before image upload',
    );
  }

  try {
    return await options.sendImageMessage({
      file: normalized,
      threadId,
      clientId: entry.clientId,
      page: entry.page,
      messageId: entry.messageId,
      onUploadComplete: ({ url }) => {
        updateOutboundMessage(options.scopeKey, entry.clientId, {
          text: url,
          uploadedUrl: true,
          threadId,
        });
        void removeOutboundImageBlob(options.scopeKey, entry.clientId);
        options.onImageUploadComplete?.(entry.clientId, url);
      },
    });
  } catch (error) {
    if (isRetryableQueueError(error)) {
      const uploadedUrl =
        readOutboundQueue(options.scopeKey).find(
          (row) => row.clientId === entry.clientId,
        )?.uploadedUrl === true;
      if (uploadedUrl) {
        updateOutboundMessage(options.scopeKey, entry.clientId, {
          uploadedUrl: true,
        });
      }
    }
    throw error;
  }
}
